
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/utils/formatters';

interface Round {
  round: number;
  ticketInvestment: number;
  capitalAllocated: number;
  aiPropProfit: number;
  withdrawal: number;
  reinvestment: number;
  botFunds: number;
  botProfit: number;
  totalAssets: number;
  cumulativeWithdrawals: number;
  cumulativeReinvestments: number;
  botFundsHistory: number[];
}

interface RoundCalculationsProps {
  rounds: Round[];
}

export const RoundCalculations: React.FC<RoundCalculationsProps> = ({ rounds }) => {
  const [expandedRounds, setExpandedRounds] = useState<Set<number>>(new Set());

  const toggleRound = (roundNumber: number) => {
    const newExpanded = new Set(expandedRounds);
    if (newExpanded.has(roundNumber)) {
      newExpanded.delete(roundNumber);
    } else {
      newExpanded.add(roundNumber);
    }
    setExpandedRounds(newExpanded);
  };

  return (
    <div className="bg-card border border-border rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-lg">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-2">
          📊 Chi Tiết Tính Toán Từng Vòng
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Phân tích chi tiết lợi nhuận qua 4 vòng (12 tháng)
        </p>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {rounds.map((round) => (
          <div key={round.round} className="bg-secondary border border-border rounded-xl sm:rounded-2xl overflow-hidden">
            {/* Round Header - Mobile Optimized */}
            <Button
              variant="ghost"
              onClick={() => toggleRound(round.round)}
              className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-secondary/80 transition-colors"
            >
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="text-xl sm:text-2xl font-bold text-primary">
                  Vòng {round.round}
                </div>
                <div className="text-sm sm:text-base text-muted-foreground">
                  (Tháng {(round.round - 1) * 3 + 1}-{round.round * 3})
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="text-right">
                  <div className="text-sm sm:text-base font-semibold text-foreground">
                    {formatCurrency(round.totalAssets)}
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Tài sản vòng</div>
                </div>
                {expandedRounds.has(round.round) ? (
                  <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </div>
            </Button>

            {/* Round Details - Collapsible */}
            {expandedRounds.has(round.round) && (
              <div className="border-t border-border">
                {/* Mobile Card Layout */}
                <div className="block sm:hidden p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-card p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">Đầu tư vé</div>
                      <div className="font-semibold text-sm">{formatCurrency(round.ticketInvestment)}</div>
                    </div>
                    <div className="bg-card p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">Vốn được cấp</div>
                      <div className="font-semibold text-sm text-tradebrand-blue">{formatCurrency(round.capitalAllocated)}</div>
                    </div>
                    <div className="bg-card p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">Lợi nhuận AI Prop</div>
                      <div className="font-semibold text-sm text-tradebrand-green">{formatCurrency(round.aiPropProfit)}</div>
                    </div>
                    <div className="bg-card p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">Lợi nhuận Bot</div>
                      <div className="font-semibold text-sm text-primary">{formatCurrency(round.botProfit)}</div>
                    </div>
                  </div>
                  
                  <div className="bg-card p-3 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-2">Phân bổ 1-3-6:</div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="text-tradebrand-green font-semibold">{formatCurrency(round.withdrawal)}</div>
                        <div className="text-muted-foreground">Rút (10%)</div>
                      </div>
                      <div className="text-center">
                        <div className="text-tradebrand-blue font-semibold">{formatCurrency(round.reinvestment)}</div>
                        <div className="text-muted-foreground">Tái đầu tư (30%)</div>
                      </div>
                      <div className="text-center">
                        <div className="text-primary font-semibold">{formatCurrency(round.botFunds)}</div>
                        <div className="text-muted-foreground">Bot (60%)</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop Table Layout */}
                <div className="hidden sm:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead className="text-xs font-bold">Đầu tư vé</TableHead>
                        <TableHead className="text-xs font-bold">Vốn được cấp</TableHead>
                        <TableHead className="text-xs font-bold">LN AI Prop</TableHead>
                        <TableHead className="text-xs font-bold">Rút (10%)</TableHead>
                        <TableHead className="text-xs font-bold">Tái đầu tư (30%)</TableHead>
                        <TableHead className="text-xs font-bold">Bot (60%)</TableHead>
                        <TableHead className="text-xs font-bold">LN Bot</TableHead>
                        <TableHead className="text-xs font-bold">Tổng tài sản</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="border-border">
                        <TableCell className="font-semibold text-sm">{formatCurrency(round.ticketInvestment)}</TableCell>
                        <TableCell className="text-tradebrand-blue text-sm">{formatCurrency(round.capitalAllocated)}</TableCell>
                        <TableCell className="text-tradebrand-green text-sm">{formatCurrency(round.aiPropProfit)}</TableCell>
                        <TableCell className="text-tradebrand-green text-sm">{formatCurrency(round.withdrawal)}</TableCell>
                        <TableCell className="text-tradebrand-blue text-sm">{formatCurrency(round.reinvestment)}</TableCell>
                        <TableCell className="text-primary text-sm">{formatCurrency(round.botFunds)}</TableCell>
                        <TableCell className="text-primary text-sm">{formatCurrency(round.botProfit)}</TableCell>
                        <TableCell className="font-bold text-foreground text-sm">{formatCurrency(round.totalAssets)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary Stats - Mobile Optimized */}
      <div className="mt-6 sm:mt-8 bg-secondary border border-border p-4 sm:p-6 rounded-xl sm:rounded-2xl">
        <h4 className="font-bold text-primary mb-4 text-base sm:text-lg">📈 Tổng Kết 4 Vòng</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="text-center">
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">Tổng đã rút</div>
            <div className="font-bold text-tradebrand-green text-sm sm:text-base">
              {formatCurrency(rounds[3]?.cumulativeWithdrawals || 0)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">Tổng tái đầu tư</div>
            <div className="font-bold text-tradebrand-blue text-sm sm:text-base">
              {formatCurrency(rounds[3]?.cumulativeReinvestments || 0)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">Tổng LN Bot</div>
            <div className="font-bold text-primary text-sm sm:text-base">
              {formatCurrency(rounds.reduce((sum, round) => sum + round.botProfit, 0))}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">Tổng tài sản</div>
            <div className="font-bold text-foreground text-sm sm:text-base">
              {formatCurrency(rounds.reduce((sum, round) => sum + round.totalAssets, 0))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
